import { useEffect, useMemo, useState } from 'react'
import { Box, Button, Slider, Typography } from '@mui/material'
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined'
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined'
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined'
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined'
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import AssignmentReturnOutlinedIcon from '@mui/icons-material/AssignmentReturnOutlined'
import LooksOneOutlinedIcon from '@mui/icons-material/LooksOneOutlined'
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined'
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined'
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined'
import ThunderstormOutlinedIcon from '@mui/icons-material/ThunderstormOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore'
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee'
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded'
import PauseRoundedIcon from '@mui/icons-material/PauseRounded'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import { C, Card, IconBubble, InfoTip, Pill, Reveal, SectionHead, dashFlow, grad, growX, pulse } from './aboutUi'
import { useSignalCount } from './signalsService'

// ─────────────────────────────────────────────────────────────────────────────
// 1. How PACE thinks: 5-stage pipeline that auto-plays; click a stage to pin it.
// ─────────────────────────────────────────────────────────────────────────────
const STAGES = [
  {
    key: 'order', title: 'Order arrives', icon: <ShoppingCartOutlinedIcon />, color: C.sky,
    short: 'Pickup, drop, weight, COD/prepaid, value',
    detail: [
      'Your platform sends the order along with the couriers that can serve it. Your existing rules and serviceability still decide who is eligible.',
      'The order includes pickup and drop pincodes, weight and size, payment mode, order value, sales channel, and each courier\'s rate-card price.',
    ],
  },
  {
    key: 'signals', title: 'Reads {n} signals', icon: <TravelExploreOutlinedIcon />, color: C.violet,
    short: 'Route, courier history, buyer area, season',
    detail: [
      'PACE adds context your team could never check by hand for every order:',
      '• How each courier has recently performed on this exact route and in this drop area',
      '• COD refusal and RTO patterns for this buyer area',
      '• Weight-dispute history, NDR call outcomes, festivals, weekends and time of day',
      'Every one of them is listed in the Signals tab, with how it is calculated.',
    ],
  },
  {
    key: 'predict', title: 'AI predicts outcomes', icon: <PsychologyOutlinedIcon />, color: C.indigo,
    short: '4 predictions for every courier',
    detail: [
      'For every eligible courier, machine-learning models trained on millions of past Prozo shipments answer four questions:',
      'Will it deliver? Will it come back (RTO)? Will it deliver on the first attempt? How many hours will it take?',
      'These are probabilities, not guesses. When PACE says 90%, about 90 of 100 such shipments really do deliver.',
    ],
  },
  {
    key: 'goals', title: 'Applies your goals', icon: <TuneOutlinedIcon />, color: C.amber,
    short: 'Speed vs cost, guardrails, SLA tier',
    detail: [
      'Business knobs turn the predictions into a single score: how much weight to give delivery success, first-attempt success, on-time performance and cost.',
      'Guardrails remove couriers that are too risky. The SLA ladder (same-day, next-day, surface and so on) makes sure the promised service level is honoured.',
      'Knobs can be set per merchant and take effect within a minute, with no retraining needed.',
    ],
  },
  {
    key: 'rank', title: 'Ranked couriers + reasons', icon: <EmojiEventsOutlinedIcon />, color: C.emerald,
    short: 'Best courier first, with the "why"',
    detail: [
      'PACE returns every courier ranked from best to worst. Each one comes with its expected delivery time, true cost and a breakdown of why it ranked where it did.',
      'If anything goes wrong, the order automatically falls back to the existing allocation, so shipping never stops.',
    ],
  },
]

function Pipeline() {
  const n = useSignalCount()
  const title = (t) => t.replace('{n}', n)
  const [active, setActive] = useState(0)
  const [playing, setPlaying] = useState(true)
  useEffect(() => {
    if (!playing) return undefined
    const id = setInterval(() => setActive((a) => (a + 1) % STAGES.length), 3200)
    return () => clearInterval(id)
  }, [playing])
  const s = STAGES[active]

  return (
    <Card hover={false} sx={{ p: { xs: 2, md: 4 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button size='small' onClick={() => setPlaying((p) => !p)} startIcon={playing ? <PauseRoundedIcon /> : <PlayArrowRoundedIcon />} sx={{ borderRadius: 99, textTransform: 'none', fontWeight: 700 }}>
          {playing ? 'Pause tour' : 'Auto-play tour'}
        </Button>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(5, 1fr)' }, gap: { xs: 1.5, md: 0 }, alignItems: 'stretch' }}>
        {STAGES.map((st, i) => {
          const on = i === active
          const done = i < active
          return (
            <Box key={st.key} sx={{ display: 'flex', flexDirection: { xs: 'row', md: 'column' }, alignItems: 'center', position: 'relative', gap: { xs: 2, md: 0 } }}>
              {i < STAGES.length - 1 && (
                <Box component='svg' sx={{ display: { xs: 'none', md: 'block' }, position: 'absolute', top: 34, left: 'calc(50% + 40px)', width: 'calc(100% - 80px)', height: 8, overflow: 'visible' }}>
                  <line x1='0' y1='4' x2='100%' y2='4' stroke={done || on ? st.color : C.line} strokeWidth='3' strokeDasharray='6 6' style={{ animation: on || done ? `${dashFlow} .8s linear infinite` : 'none' }} />
                </Box>
              )}
              <Box
                onClick={() => { setActive(i); setPlaying(false) }}
                sx={{
                  width: 68, height: 68, borderRadius: '50%', display: 'grid', placeItems: 'center', cursor: 'pointer', flexShrink: 0,
                  color: on || done ? '#fff' : st.color, background: on || done ? grad(st.color, `${st.color}BB`) : `${st.color}14`,
                  border: `2px solid ${on ? '#fff' : `${st.color}55`}`, transition: 'all .3s ease', transform: on ? 'scale(1.12)' : 'scale(1)',
                  animation: on ? `${pulse} 1.8s infinite` : 'none', '& svg': { fontSize: 30 },
                }}
              >
                {st.icon}
              </Box>
              <Box sx={{ textAlign: { xs: 'left', md: 'center' }, mt: { md: 1.5 }, px: 1, cursor: 'pointer' }} onClick={() => { setActive(i); setPlaying(false) }}>
                <Typography sx={{ fontWeight: 800, color: on ? st.color : C.slate, fontSize: 15 }}>{title(st.title)}</Typography>
                <Typography sx={{ fontSize: 12.5, color: C.muted }}>{st.short}</Typography>
              </Box>
            </Box>
          )
        })}
      </Box>
      <Box key={active} sx={{ mt: 3, p: 2.5, borderRadius: 3, background: `${s.color}0D`, border: `1px solid ${s.color}33`, display: 'flex', gap: 2, alignItems: 'flex-start', animation: 'none' }}>
        <IconBubble icon={s.icon} color={s.color} size={44} />
        <Box>
          <Typography sx={{ fontWeight: 800, color: s.color }}>Step {active + 1} · {title(s.title)}</Typography>
          {s.detail.map((d, i) => <Typography key={i} sx={{ color: C.ink, mt: 0.5, fontSize: 14.5 }}>{d}</Typography>)}
        </Box>
      </Box>
      {playing && (
        <Box sx={{ mt: 1.5, height: 3, borderRadius: 2, background: C.line, overflow: 'hidden' }}>
          <Box key={active} sx={{ height: '100%', background: s.color, transformOrigin: 'left', animation: `${growX} 3.2s linear both` }} />
        </Box>
      )}
    </Card>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// 2. The four predictions
// ─────────────────────────────────────────────────────────────────────────────
function Gauge({ value, color }) {
  const r = 34
  const circ = Math.PI * r
  return (
    <Box component='svg' viewBox='0 0 90 52' sx={{ width: 110, display: 'block', mx: 'auto' }}>
      <path d='M11 46 A34 34 0 0 1 79 46' fill='none' stroke={C.line} strokeWidth='9' strokeLinecap='round' />
      <path d='M11 46 A34 34 0 0 1 79 46' fill='none' stroke={color} strokeWidth='9' strokeLinecap='round' strokeDasharray={`${circ * value} ${circ}`} style={{ transition: 'stroke-dasharray 1s ease' }} />
      <text x='45' y='44' textAnchor='middle' fontSize='15' fontWeight='800' fill={C.slate}>{Math.round(value * 100)}%</text>
    </Box>
  )
}

function TimeRange() {
  return (
    <Box sx={{ px: 1, pt: 1.5 }}>
      <Box sx={{ position: 'relative', height: 26 }}>
        <Box sx={{ position: 'absolute', top: 10, left: 0, right: 0, height: 6, borderRadius: 3, background: C.line }} />
        <Box sx={{ position: 'absolute', top: 10, left: '18%', width: '58%', height: 6, borderRadius: 3, background: grad(C.sky, C.violet) }} />
        {[{ l: '18%', t: '38h' }, { l: '42%', t: '52h' }, { l: '76%', t: '71h' }].map((m, i) => (
          <Box key={i} sx={{ position: 'absolute', left: m.l, top: 4, transform: 'translateX(-50%)', width: 18, height: 18, borderRadius: '50%', background: '#fff', border: `3px solid ${i === 1 ? C.indigo : C.sky}` }} />
        ))}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, color: C.muted, mt: 0.5, px: '10%' }}>
        <span>Fast case 38h</span><b style={{ color: C.indigo }}>Typical 52h</b><span>Slow case 71h</span>
      </Box>
    </Box>
  )
}

const PREDICTIONS = [
  { q: 'Will it be delivered?', icon: <CheckCircleOutlineIcon />, color: C.emerald, v: 0.92,
    tip: 'The chance this courier delivers this particular order. PACE learns it from what actually happened to similar shipments on the same route, in the same payment mode and at a similar value.' },
  { q: 'Will it come back (RTO)?', icon: <AssignmentReturnOutlinedIcon />, color: C.rose, v: 0.06,
    tip: 'The chance the order is returned to origin, which means two-way freight plus a lost sale. It matters most for COD orders, where some couriers turn refusals into deliveries far better than others.' },
  { q: 'First attempt success?', icon: <LooksOneOutlinedIcon />, color: C.violet, v: 0.81,
    tip: 'The chance the order is delivered on the very first attempt and within the promised date. Every failed attempt irritates the buyer, adds a day and raises the risk of RTO.' },
  { q: 'How long will it take?', icon: <TimerOutlinedIcon />, color: C.sky, range: true,
    tip: 'A range, not one number: a fast case, a typical case and a slow case. That shows how reliable a courier is, not only how fast it is on average. A courier that is usually quick but sometimes very late is scored accordingly.' },
]

// ─────────────────────────────────────────────────────────────────────────────
// 3. Live example: the real scoring formula on an illustrative order.
// ─────────────────────────────────────────────────────────────────────────────
const EXAMPLE_COURIERS = [
  { id: 'A', name: 'Courier A', color: C.sky, price: 58, eta: 90, del: 0.90, fasr: 0.84, tat: 0.85 },
  { id: 'B', name: 'Courier B', color: C.violet, price: 92, eta: 40, del: 0.92, fasr: 0.87, tat: 0.88 },
  { id: 'C', name: 'Courier C', color: C.amber, price: 50, eta: 100, del: 0.78, fasr: 0.70, tat: 0.75 },
  { id: 'D', name: 'Courier D', color: C.rose, price: 45, eta: 96, del: 0.46, fasr: 0.40, tat: 0.55 },
]
// Weight presets mirror PACE's real defaults (balanced) and its merchant-category defaults.
const PRESETS = [
  { key: 'bal', label: 'Balanced', w: [0.40, 0.20, 0.20, 0.20], note: 'PACE default weights' },
  { key: 'cod', label: 'COD-heavy brand', w: [0.55, 0.20, 0.15, 0.10], note: 'Protects delivery % above all' },
  { key: 'pre', label: 'Prepaid, cost-focused', w: [0.30, 0.15, 0.15, 0.40], note: 'Cost carries more weight' },
]
const MIN_DELIVERY = 0.5
const L_FAST = 1 / 72
const L_CHEAP = 1 / 90
const CAP = 2

function scoreOf(k, w, alpha) {
  const penalty = alpha * L_FAST * k.eta + (1 - alpha) * L_CHEAP * k.price
  const L = CAP / (CAP + penalty)
  const parts = [w[0] * k.del, w[1] * k.fasr, w[2] * k.tat, w[3] * L]
  return { L, parts, total: parts.reduce((a, b) => a + b, 0) }
}

const STEPS = ['Eligible couriers', 'AI predictions', 'Safety guardrail', 'Score & rank']
const PART_COLORS = [C.emerald, C.violet, C.sky, C.amber]
const PART_NAMES = ['Delivery', 'First attempt', 'On-time', 'Speed + cost']

function LiveExample() {
  const [step, setStep] = useState(0)
  const [preset, setPreset] = useState('bal')
  const [alpha, setAlpha] = useState(0.5)
  const w = PRESETS.find((p) => p.key === preset).w

  const rows = useMemo(() => {
    const scored = EXAMPLE_COURIERS.map((k) => ({ ...k, ...scoreOf(k, w, alpha), blocked: k.del < MIN_DELIVERY }))
    const ranked = [...scored].sort((a, b) => (a.blocked - b.blocked) || (b.total - a.total))
    return scored.map((k) => ({ ...k, rank: step >= 3 ? ranked.findIndex((r) => r.id === k.id) : EXAMPLE_COURIERS.findIndex((r) => r.id === k.id) }))
  }, [w, alpha, step])
  const winner = rows.find((r) => r.rank === 0)
  const ROW_H = 76

  return (
    <Card hover={false} sx={{ p: { xs: 2, md: 4 } }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ p: 2, borderRadius: 3, background: grad('#EEF2FF', '#F5F3FF'), border: `1px dashed ${C.indigo}55` }}>
          <Typography sx={{ fontSize: 12, fontWeight: 800, color: C.indigo, letterSpacing: 1 }}>SAMPLE ORDER</Typography>
          <Typography sx={{ fontWeight: 700, color: C.slate }}>Mumbai 400072 → Patna 800001</Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
            <Pill color={C.rose}>COD ₹1,499</Pill><Pill color={C.sky}>0.8 kg</Pill><Pill color={C.violet}>Surface</Pill>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {STEPS.map((t, i) => (
            <Box key={t} onClick={() => setStep(i)} sx={{
              cursor: 'pointer', px: 1.75, py: 1, borderRadius: 99, fontSize: 13, fontWeight: 700, transition: 'all .2s',
              color: i <= step ? '#fff' : C.muted, background: i <= step ? grad(C.indigo, C.violet) : '#fff', border: `1px solid ${i <= step ? 'transparent' : C.line}`,
            }}>
              {i + 1}. {t}
            </Box>
          ))}
        </Box>
      </Box>

      {/* ranking board: rows move to their new rank with a smooth slide */}
      <Box sx={{ position: 'relative', height: ROW_H * EXAMPLE_COURIERS.length }}>
        {rows.map((k) => {
          const dropped = step >= 2 && k.blocked
          const top = k.rank * ROW_H
          const isWin = step >= 3 && k.rank === 0
          return (
            <Box key={k.id} sx={{
              position: 'absolute', left: 0, right: 0, top, height: ROW_H - 10, transition: 'top .7s cubic-bezier(.2,.8,.2,1), opacity .4s, filter .4s',
              display: 'grid', gridTemplateColumns: { xs: '38px 1fr', md: '48px 170px 1fr 120px' }, alignItems: 'center', gap: 2, px: 2, borderRadius: 3,
              background: isWin ? grad('#ECFDF5', '#F0FDFA') : '#fff', border: `1.5px solid ${isWin ? C.emerald : C.line}`,
              opacity: dropped ? 0.45 : 1, filter: dropped ? 'grayscale(1)' : 'none', boxShadow: isWin ? `0 10px 30px ${C.emerald}33` : 'none',
            }}>
              <Box sx={{ width: 38, height: 38, borderRadius: 2, display: 'grid', placeItems: 'center', fontWeight: 900, color: '#fff', background: grad(k.color, `${k.color}AA`) }}>
                {step >= 3 && !dropped ? `#${k.rank + 1}` : k.id}
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 800, color: C.slate, display: 'flex', alignItems: 'center', gap: 1 }}>
                  {k.name} {isWin && <Pill color={C.emerald} solid>PACE pick</Pill>}
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: C.muted }}>₹{k.price} · ~{Math.round(k.eta / 24 * 10) / 10} days</Typography>
              </Box>
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                {step === 0 && <Typography sx={{ fontSize: 13, color: C.muted }}>Eligible from your rules. Priced from its own rate card.</Typography>}
                {step >= 1 && step < 3 && (
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    {[['Deliver', k.del, C.emerald], ['1st attempt', k.fasr, C.violet], ['On-time', k.tat, C.sky]].map(([n, v, c]) => (
                      <Box key={n} sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, color: C.muted }}><span>{n}</span><b style={{ color: c }}>{Math.round(v * 100)}%</b></Box>
                        <Box sx={{ height: 7, borderRadius: 4, background: C.line, overflow: 'hidden' }}>
                          <Box sx={{ height: '100%', width: `${v * 100}%`, background: c, transformOrigin: 'left', animation: `${growX} .8s ease both` }} />
                        </Box>
                      </Box>
                    ))}
                    {dropped && <Pill color={C.rose} solid>Blocked · delivery below 50%</Pill>}
                  </Box>
                )}
                {step >= 3 && !dropped && (
                  <Box sx={{ display: 'flex', height: 18, borderRadius: 2, overflow: 'hidden', width: `${Math.min(100, k.total * 110)}%`, transition: 'width .6s ease' }}>
                    {k.parts.map((p, i) => (
                      <Box key={i} title={`${PART_NAMES[i]}: ${(p * 100).toFixed(1)} pts`} sx={{ flex: p, background: PART_COLORS[i], transition: 'flex .6s ease' }} />
                    ))}
                  </Box>
                )}
                {step >= 3 && dropped && <Pill color={C.rose} solid>Removed by guardrail</Pill>}
              </Box>
              <Box sx={{ display: { xs: 'none', md: 'block' }, textAlign: 'right' }}>
                {step >= 3 && !dropped && <Typography sx={{ fontWeight: 900, fontSize: 22, color: isWin ? C.emerald : C.slate }}>{(k.total * 100).toFixed(1)}</Typography>}
                {step >= 3 && !dropped && <Typography sx={{ fontSize: 11, color: C.muted }}>score / 100</Typography>}
              </Box>
            </Box>
          )
        })}
      </Box>

      {step >= 3 && (
        <Box sx={{ mt: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {PART_NAMES.map((n, i) => (
            <Box key={n} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: 12.5, color: C.muted }}>
              <Box sx={{ width: 12, height: 12, borderRadius: 1, background: PART_COLORS[i] }} /> {n} ({Math.round(w[i] * 100)}%)
            </Box>
          ))}
        </Box>
      )}

      {/* controls */}
      <Box sx={{ mt: 3, p: 2.5, borderRadius: 3, background: C.soft, border: `1px solid ${C.line}`, display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '1fr 1fr' }, gap: 3, opacity: step >= 3 ? 1 : 0.55, transition: 'opacity .3s' }}>
        <Box>
          <Typography sx={{ fontWeight: 800, color: C.slate, display: 'flex', alignItems: 'center' }}>
            1 · Merchant profile
            <InfoTip title='Merchant profile'>
              <p>Every merchant can have its own weights. A COD-heavy fashion brand cares most about delivery success. A prepaid electronics brand may give cost more weight.</p>
              <p>These three presets use PACE&apos;s real default weights.</p>
            </InfoTip>
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
            {PRESETS.map((p) => (
              <Box key={p.key} onClick={() => { setPreset(p.key); setStep(3) }} sx={{
                cursor: 'pointer', px: 1.5, py: 0.9, borderRadius: 2.5, fontSize: 13, fontWeight: 700, transition: 'all .2s',
                color: preset === p.key ? '#fff' : C.ink, background: preset === p.key ? grad(C.amber, C.orange) : '#fff', border: `1px solid ${preset === p.key ? 'transparent' : C.line}`,
              }}>{p.label}</Box>
            ))}
          </Box>
          <Typography sx={{ fontSize: 12.5, color: C.muted, mt: 1 }}>{PRESETS.find((p) => p.key === preset).note}</Typography>
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 800, color: C.slate, display: 'flex', alignItems: 'center' }}>
            2 · Speed ↔ cost dial
            <InfoTip title='Speed ↔ cost dial'>
              <p>This dial controls how the logistics part of the score balances a faster ETA against a cheaper price.</p>
              <p>Delivery quality still counts, so PACE will not pick a cheap courier that is likely to fail. Try it: slide to &quot;Cheapest&quot; with the Balanced profile.</p>
            </InfoTip>
          </Typography>
          <Box sx={{ px: 1 }}>
            <Slider value={alpha} min={0} max={1} step={0.05} onChange={(_, v) => { setAlpha(v); setStep(3) }}
              sx={{ color: C.indigo, '& .MuiSlider-thumb': { width: 22, height: 22, boxShadow: `0 0 0 6px ${C.indigo}22` } }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: C.muted, mt: -1 }}>
              <span><CurrencyRupeeIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} />Cheapest</span><span>Balanced</span><span><BoltOutlinedIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} />Fastest</span>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={{ mt: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Typography sx={{ color: C.ink, fontSize: 14.5, flex: 1, minWidth: 260 }}>
          {step === 0 && 'Your rules decide who is eligible. Here, four couriers can serve this pincode.'}
          {step === 1 && 'PACE predicts how each courier will perform on this specific order: this route, COD, this value.'}
          {step === 2 && 'Courier D is cheapest, but PACE expects it to deliver fewer than half of orders like this one. It is removed before it can cost you an RTO.'}
          {step >= 3 && winner && <>Winner: <b style={{ color: C.emerald }}>{winner.name}</b>. Change the profile or the dial to see how the ranking follows your goals.</>}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button disabled={step === 0} onClick={() => setStep((s) => s - 1)} sx={{ borderRadius: 99, textTransform: 'none' }}>Back</Button>
          <Button variant='contained' disabled={step === 3} onClick={() => setStep((s) => s + 1)} endIcon={<ArrowForwardRoundedIcon />}
            sx={{ borderRadius: 99, textTransform: 'none', fontWeight: 700, background: grad(C.indigo, C.violet), boxShadow: 'none' }}>
            Next step
          </Button>
        </Box>
      </Box>
      <Typography sx={{ fontSize: 11.5, color: C.muted, mt: 1.5, fontStyle: 'italic' }}>
        Illustrative couriers and predictions. The scoring maths, weights and guardrail are PACE&apos;s real defaults.
      </Typography>
    </Card>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// 4. Safety nets
// ─────────────────────────────────────────────────────────────────────────────
const SAFETY = [
  { t: 'Delivery guardrail', icon: <ShieldOutlinedIcon />, color: C.rose, d: 'Couriers unlikely to deliver are removed, however cheap they are.',
    tip: 'Any courier whose predicted delivery chance is below the threshold (50% by default, configurable per merchant) is dropped from the ranking.' },
  { t: 'SLA tier promise', icon: <BoltOutlinedIcon />, color: C.amber, d: 'Same-day, next-day or surface: the promised tier is honoured first.',
    tip: 'PACE sorts by service tier before score. If no courier offers the exact tier, it steps down the ladder in order. It only goes faster as a last resort, so it never upsells you silently. Tiers can differ by zone and time of day, for example SDD for orders placed after 5 pm and NDD during the day.' },
  { t: 'Disruption aware', icon: <ThunderstormOutlinedIcon />, color: C.sky, d: 'Floods, strikes and festival backlogs shift couriers\' ETA before orders get stuck.',
    tip: 'When Prozo ops log a courier disruption (a hub shutdown, a regional backlog, a festival surge), PACE adds the expected delay to that courier\'s ETA for the affected lanes. Couriers that are not affected naturally rise up the ranking.' },
  { t: 'Explains every decision', icon: <VisibilityOutlinedIcon />, color: C.violet, d: 'Every ranking shows its reasons: no black box.',
    tip: 'For any order, the ops team can open the explainer to see each courier\'s delivery, first-attempt, on-time and cost contributions, the true cost breakdown, and where every number came from (recent track record or AI prediction).' },
  { t: 'True cost, not list price', icon: <CurrencyRupeeIcon />, color: C.emerald, d: 'Rate-card price plus expected weight-dispute surcharges.',
    tip: 'Each courier is priced from its own rate card, plus the surcharge you are statistically likely to pay later from that courier\'s weight-discrepancy history. Couriers with no valid price are pushed to the bottom.' },
  { t: 'Automatic fallback', icon: <SettingsBackupRestoreIcon />, color: C.teal, d: 'If PACE is slow or unavailable, allocation continues on the existing engine.',
    tip: 'PACE has a strict time budget. Any error or timeout means the order is allocated by the existing rule engine, so shipping never stops. Each merchant can also be switched back with a single toggle.' },
]

// ─────────────────────────────────────────────────────────────────────────────
// 5. Learning loop
// ─────────────────────────────────────────────────────────────────────────────
const LOOP = [
  { t: 'Ship', d: 'Orders go out with PACE\'s pick', c: C.indigo, a: -90 },
  { t: 'Observe', d: 'Delivered, RTO, NDR, delays and weight disputes are captured', c: C.violet, a: 0 },
  { t: 'Refresh nightly', d: 'Courier track records updated every night', c: C.sky, a: 90 },
  { t: 'Retrain weekly', d: 'Models retrained and promoted only if they pass a quality gate', c: C.emerald, a: 180 },
]
function LearningLoop() {
  const R = 110
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '340px 1fr' }, gap: 4, alignItems: 'center' }}>
      <Box component='svg' viewBox='0 0 320 320' sx={{ width: '100%', maxWidth: 320, mx: 'auto' }}>
        <defs>
          <linearGradient id='loopG' x1='0' y1='0' x2='1' y2='1'>
            <stop offset='0%' stopColor={C.indigo} /><stop offset='100%' stopColor={C.emerald} />
          </linearGradient>
        </defs>
        <circle cx='160' cy='160' r={R} fill='none' stroke='url(#loopG)' strokeWidth='6' strokeDasharray='10 8'>
          <animateTransform attributeName='transform' type='rotate' from='0 160 160' to='360 160 160' dur='30s' repeatCount='indefinite' />
        </circle>
        <circle r='9' fill={C.amber}>
          <animateMotion dur='6s' repeatCount='indefinite' path={`M160 ${160 - R} A${R} ${R} 0 1 1 159.9 ${160 - R}`} />
        </circle>
        <text x='160' y='152' textAnchor='middle' fontSize='14' fontWeight='800' fill={C.slate}>Gets smarter</text>
        <text x='160' y='172' textAnchor='middle' fontSize='14' fontWeight='800' fill={C.slate}>every week</text>
        {LOOP.map((s) => {
          const x = 160 + R * Math.cos((s.a * Math.PI) / 180)
          const y = 160 + R * Math.sin((s.a * Math.PI) / 180)
          return (
            <g key={s.t}>
              <circle cx={x} cy={y} r='30' fill='#fff' stroke={s.c} strokeWidth='3' />
              <text x={x} y={y + 4} textAnchor='middle' fontSize='10.5' fontWeight='800' fill={s.c}>{s.t.split(' ')[0]}</text>
            </g>
          )
        })}
      </Box>
      <Box sx={{ display: 'grid', gap: 1.5 }}>
        {LOOP.map((s, i) => (
          <Reveal key={s.t} delay={i * 120}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', p: 1.75, borderRadius: 3, background: '#fff', border: `1px solid ${C.line}`, borderLeft: `5px solid ${s.c}` }}>
              <Typography sx={{ fontWeight: 900, color: s.c, fontSize: 22, width: 28 }}>{i + 1}</Typography>
              <Box>
                <Typography sx={{ fontWeight: 800, color: C.slate }}>{s.t}</Typography>
                <Typography sx={{ fontSize: 13.5, color: C.muted }}>{s.d}</Typography>
              </Box>
            </Box>
          </Reveal>
        ))}
        <Typography sx={{ fontSize: 13, color: C.muted }}>
          Recent shipments count more: the last 7 days weigh 1.5× and 8–30 days 1.25×. Lanes with very few shipments are discounted automatically, so one lucky delivery never makes a courier look great.
        </Typography>
      </Box>
    </Box>
  )
}

export default function WhatTab({ onNext }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 7 }}>
      {/* one-liner */}
      <Reveal>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2.5 }}>
          {[
            { k: 'Learn', t: 'From millions of real shipments', d: 'Every delivery, RTO, failed attempt and delay across the Prozo network becomes training data.', c: C.indigo, i: <PsychologyOutlinedIcon /> },
            { k: 'Predict', t: 'How each courier will do on each order', d: 'Not a courier\'s average: its likely outcome on this route, for this payment mode, at this value, this week.', c: C.violet, i: <TravelExploreOutlinedIcon /> },
            { k: 'Decide', t: 'The best courier for your goals', d: 'Balances delivery success, first-attempt success, speed and true cost the way you tell it to.', c: C.emerald, i: <EmojiEventsOutlinedIcon /> },
          ].map((x, i) => (
            <Card key={x.k} accent={grad(x.c, `${x.c}88`)}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <IconBubble icon={x.i} color={x.c} />
                <Box>
                  <Typography sx={{ fontSize: 12, fontWeight: 800, color: x.c, letterSpacing: 1.5 }}>STEP {i + 1} · {x.k.toUpperCase()}</Typography>
                  <Typography sx={{ fontWeight: 800, color: C.slate, lineHeight: 1.25 }}>{x.t}</Typography>
                </Box>
              </Box>
              <Typography sx={{ mt: 1.5, color: C.muted, fontSize: 14.5 }}>{x.d}</Typography>
            </Card>
          ))}
        </Box>
      </Reveal>

      <Box>
        <Reveal>
          <SectionHead kicker='Under the hood' title='How PACE decides, in 5 steps' sub='Every decision takes a fraction of a second. Click any step, or let the tour play.' />
        </Reveal>
        <Reveal delay={100}><Pipeline /></Reveal>
      </Box>

      <Box>
        <Reveal>
          <SectionHead kicker='The AI' color={C.violet} title='Four questions PACE answers for every courier, on every order'
            sub='A traditional rule engine looks at a courier’s past average. PACE predicts the outcome of this specific shipment.' />
        </Reveal>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' }, gap: 2.5 }}>
          {PREDICTIONS.map((p, i) => (
            <Reveal key={p.q} delay={i * 100}>
              <Card accent={p.color} sx={{ height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <IconBubble icon={p.icon} color={p.color} size={42} />
                  <InfoTip title={p.q} color={p.color}><p>{p.tip}</p></InfoTip>
                </Box>
                <Typography sx={{ fontWeight: 800, color: C.slate, mt: 1.5, mb: 1 }}>{p.q}</Typography>
                {p.range ? <TimeRange /> : <Gauge value={p.v} color={p.color} />}
                <Typography sx={{ fontSize: 11.5, color: C.muted, textAlign: 'center', mt: 1 }}>Example output for one courier</Typography>
              </Card>
            </Reveal>
          ))}
        </Box>
      </Box>

      <Box>
        <Reveal>
          <SectionHead kicker='Try it' color={C.amber} title='Watch PACE decide on a real-looking order'
            sub='Step through the decision, then change the merchant profile or the speed ↔ cost dial and watch the ranking re-order live.' />
        </Reveal>
        <Reveal delay={100}><LiveExample /></Reveal>
      </Box>

      <Box>
        <Reveal>
          <SectionHead kicker='Built-in safety' color={C.rose} title='Smart and safe: guardrails come as standard' sub='Tap the (i) on any card for details.' />
        </Reveal>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' }, gap: 2.5 }}>
          {SAFETY.map((s, i) => (
            <Reveal key={s.t} delay={(i % 3) * 100}>
              <Card sx={{ height: '100%', display: 'flex', gap: 2 }}>
                <IconBubble icon={s.icon} color={s.color} size={46} />
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontWeight: 800, color: C.slate }}>{s.t}</Typography>
                    <InfoTip title={s.t} color={s.color}><p>{s.tip}</p></InfoTip>
                  </Box>
                  <Typography sx={{ fontSize: 14, color: C.muted, mt: 0.5 }}>{s.d}</Typography>
                </Box>
              </Card>
            </Reveal>
          ))}
        </Box>
      </Box>

      <Box>
        <Reveal>
          <SectionHead kicker='Always learning' color={C.emerald} title='A system that improves itself' sub='No manual rule updates. PACE refreshes itself from real outcomes.' />
        </Reveal>
        <Card hover={false} sx={{ p: { xs: 2, md: 4 } }}><LearningLoop /></Card>
      </Box>

      <NextBanner label='Next: Why brands need PACE' color={C.rose} onClick={onNext} />
    </Box>
  )
}

export function NextBanner({ label, color, onClick }) {
  return (
    <Box onClick={onClick} sx={{
      cursor: 'pointer', p: 3, borderRadius: 4, color: '#fff', background: grad(color, `${color}AA`), display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      boxShadow: `0 16px 40px ${color}44`, transition: 'transform .2s', '&:hover': { transform: 'translateY(-3px)' },
    }}>
      <Typography sx={{ fontWeight: 800, fontSize: 20 }}>{label}</Typography>
      <ArrowForwardRoundedIcon sx={{ fontSize: 32 }} />
    </Box>
  )
}
